/**
 * Single source of truth for chatbot “Insert variable” placeholders.
 * Add new system variables here under the right section — the picker updates automatically.
 */

/** @typedef {{ id: string, description: string }} FlowVariableDefinition */
/** @typedef {{ id: string, label: string, variables: FlowVariableDefinition[], emptyHint?: string }} FlowVariableSection */

export const FLOW_VARIABLE_SECTION_ORDER = [
  "default",
  "ticket_booking",
  "appointment",
  "custom",
];

export const CUSTOM_SECTION_ID = "custom";
export const CUSTOM_SECTION_LABEL = "Custom (this chatbot)";

/**
 * Built-in variables grouped for the insert picker.
 * @type {Record<string, { label: string, variables: FlowVariableDefinition[] }>}
 */
export const FLOW_VARIABLE_SECTIONS = {
  default: {
    label: "Default variables",
    variables: [
      { id: "name", description: "Customer name from profile or an earlier answer." },
      { id: "phonenumber", description: "Customer WhatsApp phone number." },
      { id: "email", description: "Email if collected in the flow." },
      { id: "address", description: "Address if collected in the flow." },
      { id: "date", description: "Date saved earlier in the conversation." },
      { id: "orderitems", description: "Order line summary (e-commerce checkout)." },
      { id: "productitems", description: "Current cart items (this visit). Run Cart: Refresh cart first." },
      { id: "invaliditems", description: "Out-of-stock or insufficient-stock lines. Run Cart: Refresh cart or Check out-of-stock first." },
      { id: "cart.stock_check_failed", description: "true when inventory API failed — route to retry sync, not address." },
      { id: "cart.stock_check_error", description: "Short error hint when stock check failed (optional in messages)." },
      { id: "oldcartitems", description: "Old cart items from a previous visit. Run Cart: Refresh cart first." },
    ],
  },
  ticket_booking: {
    label: "Ticket booking",
    variables: [
      { id: "ticketbooking", description: "Latest ticket booking summary (after reserve or payment)." },
      { id: "tour_package_details", description: "Package name, destination, inclusions — after List travel dates." },
      { id: "travel_dates_text", description: "Travel dates with duration and slots left per date — after List travel dates." },
      { id: "travel_dates_count", description: "Number of listed travel dates." },
      { id: "travel_itinerary_text", description: "Day-by-day plan for the selected departure — after List itinerary." },
      { id: "hotel_options_text", description: "Optional hotels — after Show hotels & vehicles." },
      { id: "vehicle_options_text", description: "Optional vehicles — after Show hotels & vehicles." },
      { id: "travel_price_summary", description: "Price breakdown for all travellers (needs member_count)." },
      { id: "ticket_availability_text", description: "Seats / capacity summary — after Check availability." },
      { id: "ticket_available_seats_text", description: "Open seats only — after Check availability." },
      { id: "ticket_seats_text", description: "Full seat map — after Check availability." },
      { id: "ticket_available_seats_count", description: "Count of open seats." },
      { id: "ticket_seats_count", description: "Total configured seats." },
    ],
  },
  appointment: {
    label: "Appointment booking",
    variables: [
      { id: "appointmentbooking", description: "Latest appointment summary (after reserve or confirm)." },
      { id: "appointment_slots_text", description: "Open time slots — after Check slots." },
      { id: "appointment_slots_count", description: "Number of slots returned." },
    ],
  },
};

const CUSTOM_EMPTY_HINT =
  "No custom variables yet. Add a Question (or Form) node above this message and set its Answer variable name.";

/** All built-in variable ids (flat). */
export const DEFAULT_FLOW_INSERT_VARIABLES = FLOW_VARIABLE_SECTION_ORDER.filter(
  (sectionId) => sectionId !== CUSTOM_SECTION_ID,
).flatMap((sectionId) =>
  FLOW_VARIABLE_SECTIONS[sectionId].variables.map((variable) => variable.id),
);

const builtinIdSet = new Set(DEFAULT_FLOW_INSERT_VARIABLES);

/**
 * @param {string[]} [customVariables] Answer variable names from Question / Form nodes in this flow
 * @returns {FlowVariableSection[]}
 */
export function buildFlowVariableSections(customVariables = []) {
  const sections = FLOW_VARIABLE_SECTION_ORDER.filter((id) => id !== CUSTOM_SECTION_ID).map(
    (sectionId) => ({
      id: sectionId,
      label: FLOW_VARIABLE_SECTIONS[sectionId].label,
      variables: FLOW_VARIABLE_SECTIONS[sectionId].variables.map((variable) => ({ ...variable })),
    }),
  );

  const custom = (Array.isArray(customVariables) ? customVariables : [])
    .map((id) => String(id || "").trim())
    .filter((id) => id && !builtinIdSet.has(id));

  const uniqueCustom = [...new Set(custom)];

  sections.push({
    id: CUSTOM_SECTION_ID,
    label: CUSTOM_SECTION_LABEL,
    variables: uniqueCustom.map((id) => ({
      id,
      description: `Answer saved from Question/Form step "${id}" earlier in this chatbot.`,
    })),
    emptyHint: uniqueCustom.length ? undefined : CUSTOM_EMPTY_HINT,
  });

  return sections;
}

/**
 * Flat catalog for lookups.
 * @returns {{ id: string, label: string, description: string, sectionId: string, sectionLabel: string }[]}
 */
export function buildFlowVariableCatalog(customVariables = []) {
  return buildFlowVariableSections(customVariables).flatMap((section) =>
    section.variables.map((variable) => ({
      id: variable.id,
      label: variable.id,
      description: variable.description,
      sectionId: section.id,
      sectionLabel: section.label,
      group: section.label,
    })),
  );
}

export function getFlowVariableMeta(variableId, catalog) {
  const rows = Array.isArray(catalog) ? catalog : [];
  return rows.find((row) => row.id === variableId) || null;
}

const ANSWER_VARIABLE_NODE_TYPES = new Set([
  "questionMessage",
  "interactiveMessage",
  "flowMessage",
]);

/**
 * Answer variable names defined on Question / Interactive / Form nodes in a flow.
 * @param {Array<{ type?: string, data?: { answerVariable?: string } }>} flowNodes
 * @returns {string[]}
 */
export function collectFlowAnswerVariables(flowNodes = []) {
  return Array.from(
    new Set(
      (Array.isArray(flowNodes) ? flowNodes : [])
        .filter((node) => ANSWER_VARIABLE_NODE_TYPES.has(node?.type))
        .map((node) => String(node?.data?.answerVariable || "").trim())
        .filter(Boolean),
    ),
  );
}

/**
 * Custom (flow-defined) variables only — for mapping Vertical Action inputs to Question answers.
 * @param {string[]} [customVariables]
 * @returns {FlowVariableDefinition[]}
 */
export function getCustomFlowAnswerVariables(customVariables = []) {
  const customSection = buildFlowVariableSections(customVariables).find(
    (section) => section.id === CUSTOM_SECTION_ID,
  );
  return customSection?.variables || [];
}

/** @deprecated Use FlowVariableInsertPicker instead. */
export const FLOW_INSERT_VARIABLES_HELP =
  "Choose a variable from the dropdown. Sections: Default, Ticket booking, Appointment, and Custom.";

/** @deprecated Use FLOW_VARIABLE_SECTIONS */
export const FLOW_VARIABLE_GROUPS = FLOW_VARIABLE_SECTION_ORDER.filter(
  (id) => id !== CUSTOM_SECTION_ID,
).map((id) => ({
  label: FLOW_VARIABLE_SECTIONS[id].label,
  variables: FLOW_VARIABLE_SECTIONS[id].variables,
}));
